import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/utils/cn';
import { localizedField } from '@/utils/localizedField';
import { useCategoryLabels } from '@/hooks/useCategoryLabels';
import { ONBOARDING_CLOSED_EVENT } from '@/constants/events';
import { useAuth } from '@/hooks/useAuth';
import { LearningCard } from '@/components/molecules/LearningCard';
import { Button } from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/Spinner';
import { Card } from '@/components/molecules/Card';
import { ErrorState } from '@/components/molecules/ErrorState';
import { ViewToggle, type ViewMode } from '@/components/atoms/ViewToggle';
import { StoryCarousel } from '@/components/organisms/StoryCarousel';
import { ROUTES, buildRoute } from '@/constants/routes';
import { getWeeklyAssignments, getAllUserAssignments, type WeeklyAssignmentsResponse, type WeeklyAssignment } from '@/services/api/weeklyAssignments.api';
import { usePageTransitionEntrance } from '@/context/PageTransitionEntranceContext';
import { entranceAwareBlockTransition } from '@/constants/storyResourcesEntrance';
import { RevealOnScroll } from '@/components/atoms/RevealOnScroll';
import styles from './StoriesPage.module.scss';

const EASE: [number, number, number, number] = [0.42, 0, 0.58, 1];
// Tamaño de página por sección: menos en móvil para no cargar de más.
const MOBILE_PAGE_SIZE = 4;
const DESKTOP_PAGE_SIZE = 6;
const getPageSize = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
    ? MOBILE_PAGE_SIZE
    : DESKTOP_PAGE_SIZE;
const CARD_INITIAL_BASE_DELAY = 0.5;
const CARD_INDEX_STAGGER = 0.025;
const CARD_INDEX_MAX_STAGGER = 0.15;
const HIDDEN = { opacity: 0, y: 20 };
const VISIBLE = { opacity: 1, y: 0 };

export function StoriesPage() {
  const { t, i18n } = useTranslation();
  const { labelFor } = useCategoryLabels();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pageReady = usePageTransitionEntrance();
  const reducedMotion = Boolean(useReducedMotion());

  const fadeUp = (delay: number) => ({
    initial: HIDDEN,
    animate: pageReady ? VISIBLE : HIDDEN,
    transition: entranceAwareBlockTransition(pageReady, reducedMotion, 0.42, delay),
  });

  const navState = location.state as {
    scrollToStoryId?: string;
    scrollY?: number;
    viewMode?: ViewMode;
    returnFromStory?: boolean;
    // Nº de cards cargadas en cada subsección al salir a la historia (para
    // restaurarlas al volver, igual que en Conversaciones).
    forYouLoaded?: number;
    moreLoaded?: number;
  } | null;
  const scrollToStoryId = navState?.scrollToStoryId;
  const savedScrollY = navState?.scrollY;
  const viewModeFromState = navState?.viewMode;
  const [viewMode, setViewMode] = useState<ViewMode>(viewModeFromState ?? 'card');

  // ¿Volvemos de una historia? Entonces las cards aparecen sin animación de
  // entrada (no re-animar al restaurar/subir).
  const isRestore = !!navState?.returnFromStory;

  // Weekly assignments (carrusel) — sin cambios.
  const [weeklyAssignments, setWeeklyAssignments] = useState<WeeklyAssignmentsResponse | null>(null);
  const [isLoadingWeekly, setIsLoadingWeekly] = useState(true);
  const [weeklyError, setWeeklyError] = useState(false);

  // Se incrementa al cerrarse el onboarding (asigna historias) para recargar.
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Grid "Todas las historias": dos subsecciones paginadas independientes.
  const [forYou, setForYou] = useState<WeeklyAssignment[]>([]);
  const [more, setMore] = useState<WeeklyAssignment[]>([]);
  const [forYouOffset, setForYouOffset] = useState(0);
  const [moreOffset, setMoreOffset] = useState(0);
  const [forYouHasMore, setForYouHasMore] = useState(false);
  const [moreHasMore, setMoreHasMore] = useState(false);
  const [loadingForYouMore, setLoadingForYouMore] = useState(false);
  const [loadingMoreMore, setLoadingMoreMore] = useState(false);
  const [forYouLoadMoreVisible, setForYouLoadMoreVisible] = useState(false);
  const [moreLoadMoreVisible, setMoreLoadMoreVisible] = useState(false);
  // Latch: "Más historias" no se muestra hasta que la última card de "Para ti"
  // (y su botón "Cargar más") están visibles. Una vez activado se queda (no se
  // oculta al cargar más en "Para ti").
  const [forYouReady, setForYouReady] = useState(false);
  const [isLoadingAll, setIsLoadingAll] = useState(true);
  const [allError, setAllError] = useState(false);

  // Load weekly assignments
  useEffect(() => {
    const loadWeeklyAssignments = async () => {
      if (!user?.level || !user?.id) {
        setIsLoadingWeekly(false);
        return;
      }
      try {
        setIsLoadingWeekly(true);
        setWeeklyError(false);
        const assignments = await getWeeklyAssignments();
        setWeeklyAssignments(assignments);
      } catch (error) {
        console.error('Failed to load weekly assignments:', error);
        setWeeklyAssignments(null);
        setWeeklyError(true);
      } finally {
        setIsLoadingWeekly(false);
      }
    };
    void loadWeeklyAssignments();
  }, [user?.level, user?.id, refreshNonce]);

  // Carga inicial de las dos subsecciones (y restauración al volver de una
  // historia: recargamos cada subsección hasta lo que había cargado).
  useEffect(() => {
    if (!user?.id || !user?.level) {
      setIsLoadingAll(false);
      return;
    }
    const st = navState;
    const restore = !!st?.returnFromStory;
    const pageSize = getPageSize();
    const fyLimit = restore && st?.forYouLoaded ? Math.max(st.forYouLoaded, pageSize) : pageSize;
    const mLimit = restore && st?.moreLoaded ? Math.max(st.moreLoaded, pageSize) : pageSize;

    let cancelled = false;
    void (async () => {
      try {
        setIsLoadingAll(true);
        setAllError(false);
        // Al volver de una historia mostramos "Más" y los botones desde el
        // principio (cards sin animación); en carga normal van gateados hasta que
        // la última card de cada sección termine de animar.
        setForYouReady(restore);
        setForYouLoadMoreVisible(restore);
        setMoreLoadMoreVisible(restore);
        const [fy, m] = await Promise.all([
          getAllUserAssignments(fyLimit, 0, 'interest'),
          getAllUserAssignments(mLimit, 0, 'other'),
        ]);
        if (cancelled) return;
        setForYou(fy.assignments);
        setForYouOffset(fy.assignments.length);
        setForYouHasMore(!!fy.hasMore);
        setMore(m.assignments);
        setMoreOffset(m.assignments.length);
        setMoreHasMore(!!m.hasMore);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load all stories:', error);
        setForYou([]);
        setMore([]);
        setForYouHasMore(false);
        setMoreHasMore(false);
        setAllError(true);
      } finally {
        if (!cancelled) setIsLoadingAll(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // navState se lee al montar; al volver de la historia el componente se remonta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.level, refreshNonce]);

  // Recargar al cerrarse el onboarding.
  useEffect(() => {
    const handler = () => setRefreshNonce((n) => n + 1);
    window.addEventListener(ONBOARDING_CLOSED_EVENT, handler);
    return () => window.removeEventListener(ONBOARDING_CLOSED_EVENT, handler);
  }, []);

  const loadMoreForYou = async () => {
    if (loadingForYouMore || !forYouHasMore) return;
    setLoadingForYouMore(true);
    try {
      const res = await getAllUserAssignments(getPageSize(), forYouOffset, 'interest');
      setForYou((prev) => [...prev, ...res.assignments]);
      setForYouOffset((prev) => prev + res.assignments.length);
      setForYouHasMore(!!res.hasMore);
    } catch (error) {
      console.error('Failed to load more (for you):', error);
    } finally {
      setLoadingForYouMore(false);
    }
  };

  const loadMoreMore = async () => {
    if (loadingMoreMore || !moreHasMore) return;
    setLoadingMoreMore(true);
    try {
      const res = await getAllUserAssignments(getPageSize(), moreOffset, 'other');
      setMore((prev) => [...prev, ...res.assignments]);
      setMoreOffset((prev) => prev + res.assignments.length);
      setMoreHasMore(!!res.hasMore);
    } catch (error) {
      console.error('Failed to load more (more):', error);
    } finally {
      setLoadingMoreMore(false);
    }
  };

  // Restaurar scroll al volver de una historia.
  const scrollRestoredRef = useRef(false);
  const isDataReady = !isLoadingWeekly && !isLoadingAll && forYou.length + more.length > 0;
  useEffect(() => {
    if (!scrollToStoryId || scrollRestoredRef.current || !isDataReady) return;
    const timer = setTimeout(() => {
      scrollRestoredRef.current = true;
      if (savedScrollY != null) {
        window.scrollTo(0, savedScrollY);
      } else {
        const el = document.getElementById(`story-card-${scrollToStoryId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      navigate(ROUTES.STUDENT.STORIES, { replace: true, state: { viewMode, returnFromStory: true } });
    }, 100);
    return () => clearTimeout(timer);
  }, [scrollToStoryId, savedScrollY, isDataReady, navigate, viewMode]);

  const hasForYou = forYou.length > 0;
  const totalGrid = forYou.length + more.length;

  const renderStoryCards = (
    items: WeeklyAssignment[],
    indexOffset: number,
    onLastComplete?: () => void
  ) =>
    items.map((story, i) => {
      const globalIdx = indexOffset + i;
      const isLast = i === items.length - 1;
      const cardDelay =
        CARD_INITIAL_BASE_DELAY + Math.min(globalIdx * CARD_INDEX_STAGGER, CARD_INDEX_MAX_STAGGER);
      return (
        <RevealOnScroll
          key={story.id}
          id={`story-card-${story.id}`}
          delay={cardDelay}
          disabled={reducedMotion || isRestore}
          ready={pageReady}
          onShown={isLast ? onLastComplete : undefined}
        >
          <LearningCard
            use="story"
            style="wide"
            compact={viewMode === 'list'}
            title={localizedField(story, 'title', i18n.language)}
            description={localizedField(story, 'description', i18n.language)}
            tag={labelFor(story.category)}
            xpReward={story.xpReward}
            estimatedTime={story.estimatedTime}
            completed={story.assignmentStatus === 'completed'}
            backgroundImage={story.backgroundImage}
            onClick={() =>
              navigate(buildRoute(ROUTES.STUDENT.STORY, { id: story.id }), {
                state: {
                  fromStoriesTab: 'all',
                  scrollY: window.scrollY,
                  viewMode,
                  forYouLoaded: forYou.length,
                  moreLoaded: more.length,
                },
              })
            }
          />
        </RevealOnScroll>
      );
    });

  const loadMoreButton = (
    onClick: () => void,
    loading: boolean,
    visible: boolean,
    keyVal: number
  ) => (
    <motion.div
      key={keyVal}
      className={styles.storiesPage__loadMore}
      initial={!reducedMotion ? { opacity: 0, y: 16 } : false}
      animate={!reducedMotion ? (visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }) : undefined}
      transition={{ duration: 0.38, ease: EASE }}
    >
      <Button variant="primary" onClick={onClick} disabled={loading}>
        {loading ? t('common.loading') : t('storiesPage.loadMore')}
      </Button>
    </motion.div>
  );

  return (
    <div className={styles.storiesPage}>
      <motion.div className={styles.storiesPage__header} {...fadeUp(0)}>
        <h1 className={styles.storiesPage__title}>{t('storiesPage.title')}</h1>
        <p className={styles.storiesPage__subtitle}>{t('storiesPage.subtitle')}</p>
      </motion.div>

      <motion.div className={styles.storiesPage__section} {...fadeUp(0.18)}>
        {isLoadingWeekly ? (
          <div className={styles.storiesPage__loading}>
            <Spinner size="lg" aria-label={t('common.loading')} />
          </div>
        ) : !user?.level ? (
          <Card className={styles.storiesPage__empty}>
            <p>{t('dashboard.noLevelCta')}</p>
            <Button variant="primary" onClick={() => navigate(ROUTES.STUDENT.LEVEL_TEST)}>
              {t('dashboard.takeLevelTest')}
            </Button>
          </Card>
        ) : weeklyError ? (
          <ErrorState onRetry={() => setRefreshNonce((n) => n + 1)} />
        ) : !weeklyAssignments || weeklyAssignments.totalAssigned === 0 ? (
          <Card className={styles.storiesPage__empty}>
            <p>{t('storiesPage.comingSoon')}</p>
          </Card>
        ) : (
          <StoryCarousel
            title={t('storiesPage.weeklyCarouselTitle')}
            stories={weeklyAssignments.stories.map((story) => ({
              title: localizedField(story, 'title', i18n.language),
              description: localizedField(story, 'description', i18n.language),
              category: labelFor(story.category),
              estimatedTime: story.estimatedTime,
              xpReward: story.xpReward,
              completed: story.assignmentStatus === 'completed',
              backgroundImage: story.backgroundImage,
              variant: 'featured' as const,
              onClick: () =>
                navigate(buildRoute(ROUTES.STUDENT.STORY, { id: story.id }), {
                  state: { fromStoriesTab: 'weekly', scrollY: window.scrollY },
                }),
            }))}
          />
        )}
      </motion.div>

      {!isLoadingAll && allError && (
        <ErrorState onRetry={() => setRefreshNonce((n) => n + 1)} />
      )}

      {!isLoadingAll && !allError && totalGrid > 0 && (
        <div className={styles.storiesPage__allSection}>
          <motion.div className={styles.storiesPage__viewToggleRow} {...fadeUp(0.34)}>
            <h2 className={styles.storiesPage__allTitle}>{t('storiesPage.allStoriesTitle')}</h2>
            <ViewToggle value={viewMode} onValueChange={setViewMode} aria-label={t('storiesPage.viewModeAria')} />
          </motion.div>

          <div className={styles.storiesPage__gridBlock}>
            {hasForYou && (
              <div className={styles.storiesPage__gridSection}>
                <motion.h3 className={styles.storiesPage__sectionSubTitle} {...fadeUp(0.42)}>
                  {t('storiesPage.forYou')}
                </motion.h3>
                <div
                  className={cn(
                    styles.storiesPage__grid,
                    viewMode === 'list' && styles['storiesPage__grid--list']
                  )}
                >
                  {renderStoryCards(forYou, 0, () => {
                    setForYouLoadMoreVisible(true);
                    setForYouReady(true);
                  })}
                </div>
                {forYouHasMore &&
                  loadMoreButton(
                    () => void loadMoreForYou(),
                    loadingForYouMore,
                    forYouLoadMoreVisible,
                    forYou.length
                  )}
              </div>
            )}

            {more.length > 0 && (!hasForYou || reducedMotion || forYouReady) && (
              <div className={styles.storiesPage__gridSection}>
                {hasForYou && (
                  <motion.h3
                    className={styles.storiesPage__sectionSubTitle}
                    initial={!reducedMotion ? { opacity: 0, y: 16 } : false}
                    whileInView={pageReady && !reducedMotion ? { opacity: 1, y: 0 } : undefined}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.42, ease: EASE }}
                  >
                    {t('storiesPage.moreStories')}
                  </motion.h3>
                )}
                <div
                  className={cn(
                    styles.storiesPage__grid,
                    viewMode === 'list' && styles['storiesPage__grid--list']
                  )}
                >
                  {renderStoryCards(more, forYou.length, () => setMoreLoadMoreVisible(true))}
                </div>
                {moreHasMore &&
                  loadMoreButton(
                    () => void loadMoreMore(),
                    loadingMoreMore,
                    moreLoadMoreVisible,
                    more.length
                  )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StoriesPage;
